import { License } from "../constants/license.enum"
import { EnumLicense, UserT } from "../store/slices/user.slice"
import { getNextBillingDate } from "./date.helpers"

export enum UpgradeAction {
    Upgrade = "upgrade",
    Downgrade = "downgrade",
    KeepUnlimited = "keepUnlimited",
}

export enum UserLicenseType {
    Free = "free",
    Unlimited = "unlimited",
    UnlimitedPlanDowngraded = "unlimited-plan-downgraded",
    UnlimitedPlanExpiredToFree = "unlimited-plan-expired-to-free",
    UnlimitedPlanRevoked = "unlimited-plan-revoked",
}
export type UserLicenseState = {
    type: UserLicenseType | undefined
    started?: Date | null
    nextBillingDate?: Date | null
    expired?: Date | null
    createdAt?: Date
}

export const getUserLicenseState = (
    license: EnumLicense | undefined,
    started?: Date | null,
    expired?: Date | null,
    createdAt?: Date,
    isRevoked?: boolean
): UserLicenseState => {
    const now = Date.now()

    if (license === undefined) {
        return {
            type: UserLicenseType.Free,
            createdAt,
        }
    }
    if (license === License.Free) {
        return {
            type: UserLicenseType.Free,
            createdAt,
        }
    }
    if (license === License.Enterprise && expired === null && started) {
        return {
            type: UserLicenseType.Unlimited,
            started,
            nextBillingDate: getNextBillingDate(started!),
        }
    }
    if (expired && expired?.getTime() > now && started && !isRevoked) {
        return {
            type: UserLicenseType.UnlimitedPlanDowngraded,
            expired,
        }
    }
    if (expired && expired?.getTime() < now && started && !isRevoked) {
        return { type: UserLicenseType.UnlimitedPlanExpiredToFree, expired }
    }
    if (expired && expired?.getTime() < now && started && isRevoked) {
        return { type: UserLicenseType.UnlimitedPlanRevoked, expired }
    }
    // check user license type for old data in db
    if (expired && expired?.getTime() < now && !started) {
        return { type: UserLicenseType.Free, createdAt }
    }

    return {
        type: UserLicenseType.Free,
        createdAt,
    }
}

export const getUserLicenseStateOfUser = (user: UserT): UserLicenseState =>
    getUserLicenseState(
        user.license?.license,
        user.license?.started,
        user.license?.expired,
        user.createdAt,
        user.license?.isRevoked
    )

//**
// date the license state refers to, null for users without a license
//  */
export const getLicenseDate = (state: UserLicenseState): Date | null | undefined => {
    if (state.type === UserLicenseType.Free) return null
    if (state.type === UserLicenseType.Unlimited) return state.nextBillingDate
    return state.expired
}

//**
// translation key suffix describing what the license date means
//  */
export const getLicenseDateLabel = (state: UserLicenseState): string | null => {
    switch (state.type) {
        case UserLicenseType.Unlimited:
            return "nextBilling"
        case UserLicenseType.UnlimitedPlanDowngraded:
            return "validUntil"
        case UserLicenseType.UnlimitedPlanExpiredToFree:
        case UserLicenseType.UnlimitedPlanRevoked:
            return "expired"
        default:
            return null
    }
}

//**
// for better understanding of what userLicenseType is able to do with witch action
//  */
export const getActionFromLicense = (
    licenseType: UserLicenseType | undefined
): UpgradeAction | null => {
    switch (licenseType) {
        case UserLicenseType.Free:
            return UpgradeAction.Upgrade
        case UserLicenseType.Unlimited:
            return UpgradeAction.Downgrade
        case UserLicenseType.UnlimitedPlanDowngraded:
            return UpgradeAction.KeepUnlimited
        default:
            return null
    }
}
